import { Trans } from "react-i18next";
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { toast } from "react-toastify";
import apiClient from "@shared/services/apiClient";
import {
  BAD_CHANNEL_REASON_CODES,
  BadChannelMark,
  RereferencePreview,
} from "./customizationTypes";
import {
  getErrorMessage,
  toDateTimeLabel,
  unwrapApiData,
} from "./customizationUtils";
import { useRecordingChannels } from "./customizationHooks";
import "./customizationPanels.css";

type BadChannelManagerPanelProps = {
  recordingId: string;
  recordingLabel?: string;
};

const BadChannelManagerPanel: React.FC<BadChannelManagerPanelProps> = ({
  recordingId,
  recordingLabel,
}) => {
  const [rows, setRows] = useState<BadChannelMark[]>([]);
  const [loading, setLoading] = useState(false);
  const [channelName, setChannelName] = useState("");
  const { channels, loading: channelsLoading } =
    useRecordingChannels(recordingId);
  const [reasonCode, setReasonCode] = useState(BAD_CHANNEL_REASON_CODES[0]);
  const [note, setNote] = useState("");
  const [previewExclusions, setPreviewExclusions] = useState<string[]>([]);
  const [preview, setPreview] = useState<RereferencePreview | null>(null);

  const loadBadChannels = useCallback(async () => {
    if (!recordingId.trim()) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.get(
        `/eeg/recordings/${recordingId.trim()}/bad-channels`,
      );
      const data = unwrapApiData<BadChannelMark[]>(response);
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [recordingId]);

  useEffect(() => {
    loadBadChannels();
  }, [loadBadChannels]);

  const addBadChannel = async () => {
    if (!recordingId.trim()) {
      toast.error("Recording ID is required.");
      return;
    }
    if (!channelName.trim()) {
      toast.error("Channel name is required.");
      return;
    }
    try {
      await apiClient.post(
        `/eeg/recordings/${recordingId.trim()}/bad-channels`,
        {
          channel_name: channelName.trim(),
          reason_code: reasonCode,
          note: note.trim() || undefined,
        },
      );
      toast.success("Bad channel marked.");
      setChannelName("");
      setNote("");
      await loadBadChannels();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const resolveBadChannel = async (targetChannelName: string) => {
    if (!recordingId.trim()) return;
    try {
      await apiClient.delete(
        `/eeg/recordings/${recordingId.trim()}/bad-channels/${encodeURIComponent(targetChannelName)}`,
      );
      toast.success(`Resolved ${targetChannelName}.`);
      await loadBadChannels();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const previewRereference = async () => {
    if (!recordingId.trim()) {
      toast.error("Recording ID is required.");
      return;
    }
    try {
      const response = await apiClient.post(
        `/eeg/recordings/${recordingId.trim()}/rereference-preview`,
        {
          exclude_channels: previewExclusions,
        },
      );
      setPreview(unwrapApiData<RereferencePreview>(response));
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <Card className="eeg-customization-panel">
      <Card.Body>
        <h5 className="eeg-customization-panel__title">
          Bad Channels ({recordingLabel || recordingId || "No recording"})
        </h5>
        <p className="eeg-customization-panel__hint">
          <Trans ns="features" i18nKey="ui.copy.58a9a4707bff" />
        </p>
        {!recordingId.trim() && (
          <Alert variant="warning">
            <Trans ns="features" i18nKey="ui.copy.3fac9e884759" />
          </Alert>
        )}
        <Row className="g-2 eeg-customization-panel__form-row">
          <Col md={3}>
            <Form.Select
                size="sm"
                value={channelName}
                onChange={(event) => setChannelName(event.target.value)}
                disabled={channelsLoading || channels.length === 0}
              >
                <option value=""><Trans ns="features" i18nKey="ui.copy.910859289419" /></option>
                {channels.map((ch) => (
                  <option key={ch} value={ch}>
                    {ch}
                  </option>
                ))}
              </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Select
              size="sm"
              value={reasonCode}
              onChange={(event) => setReasonCode(event.target.value)}
            >
              {BAD_CHANNEL_REASON_CODES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Control
              size="sm"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Optional note"
            />
          </Col>
          <Col md={2} className="d-grid">
            <Button size="sm" onClick={addBadChannel} disabled={!recordingId.trim()}>
              <Trans ns="features" i18nKey="ui.copy.437f20537857" />
            </Button>
          </Col>
        </Row>
        <div className="eeg-customization-panel__toolbar">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={loadBadChannels}
            disabled={!recordingId.trim()}
          >
            <Trans ns="features" i18nKey="ui.copy.56e3badc4e6c" />
          </Button>
        </div>
        {loading ? (
          <Spinner animation="border" size="sm" />
        ) : rows.length === 0 ? (
          <Alert variant="light" className="mb-3">
            <Trans ns="features" i18nKey="ui.copy.e7112effd188" /></Alert>
        ) : (
          <Table bordered hover size="sm" className="mb-4">
            <thead>
              <tr>
                <th><Trans ns="features" i18nKey="ui.copy.879f0b1bef59" /></th>
                <th><Trans ns="features" i18nKey="ui.copy.f219cc0614ae" /></th>
                <th><Trans ns="features" i18nKey="ui.copy.2c924e308820" /></th>
                <th><Trans ns="features" i18nKey="ui.copy.9fa757f616ae" /></th>
                <th><Trans ns="features" i18nKey="ui.copy.97c89a4d6630" /></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.channel_name}</td>
                  <td>{row.reason_code}</td>
                  <td>{row.note || "-"}</td>
                  <td>{toDateTimeLabel(row.marked_at)}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => resolveBadChannel(row.channel_name)}
                    >
                      <Trans ns="features" i18nKey="ui.copy.ac7f958cc028" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <Card bg="light">
          <Card.Body>
            <h6><Trans ns="features" i18nKey="ui.copy.262cdc584350" /></h6>
            <Form.Select
              multiple
              size="sm"
              value={previewExclusions}
              onChange={(event) => setPreviewExclusions(Array.from(event.target.selectedOptions, (option) => option.value))}
              className="mb-2"
              aria-label="Additional recording channels to exclude"
            >
              {channels.map((channel) => <option key={channel} value={channel}>{channel}</option>)}
            </Form.Select>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={previewRereference}
              disabled={!recordingId.trim()}
            >
              <Trans ns="features" i18nKey="ui.copy.9b151f9293b3" /></Button>
            {preview && (
              <div className="mt-3">
                <div>
                  <strong><Trans ns="features" i18nKey="ui.copy.d28a34979f62" /></strong>{" "}
                  {preview.excluded_channels.join(", ") || "None"}
                </div>
                <div>
                  <strong><Trans ns="features" i18nKey="ui.copy.973d40059019" /></strong>{" "}
                  {preview.auto_excluded.join(", ") || "None"}
                </div>
                <div>
                  <strong><Trans ns="features" i18nKey="ui.copy.1c71094c572b" /></strong>{" "}
                  {preview.manually_excluded.join(", ") || "None"}
                </div>
                <div className="text-muted small">{preview.note}</div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Card.Body>
    </Card>
  );
};

export default BadChannelManagerPanel;
